import {
    getIsInitialBreadcrumb,
    getInitialBreadcrumbs,
    findNavigationTreeForSelectedAxis,
    getCurrentBreadcrumbNode,
    setNewBreadcrumbs,
} from '../helpers/breadcrumbs';
import { BreadCrumbNodeT, NavigationTree } from '../types';
import { TFunction } from 'i18next';

type RollbackBreadcrumbsFunction = (breadcrumb: BreadCrumbNodeT) => void;

/**
 * This function handle the click on the different breadcrumbs element. It is used to rollback the breadcrumbs to the selected one.
 * @param {BreadCrumbNodeT[]} breadcrumbs - the chart's current breadcrumbs
 * @param {NavigationTree[] | undefined} navigationTrees - the navigation trees of the chart dimensions
 * @param {QueryFilterT[]} queryFilters - the chart's query filters
 * @param {React.Dispatch<React.SetStateAction<BreadCrumbNodeT[]>>} setBreadcrumbs - setter of the chart's breadcrumbs
 * @param {React.Dispatch<React.SetStateAction<QueryFilterT | undefined>>} setNavigationFilter - setter of the chart's navigation filter. It is highly correlated with the breadcrumbs so we might want to merge them in the future.
 * @returns {RollbackBreadcrumbsFunction}
 */
export const useRollbackBreadcrumbs =
    (
        breadcrumbs: BreadCrumbNodeT[],
        navigationTrees: NavigationTree[] | undefined,
        queryFilters: QueryFilterT[],
        setBreadcrumbs: React.Dispatch<React.SetStateAction<BreadCrumbNodeT[]>>,
        setNavigationFilter: React.Dispatch<
            React.SetStateAction<QueryFilterT | undefined>
        >,
        t: TFunction,
    ): RollbackBreadcrumbsFunction =>
    (breadcrumb: BreadCrumbNodeT) => {
        const isInitialBreadcrumb = getIsInitialBreadcrumb(breadcrumb.value);
        if (isInitialBreadcrumb) {
            setBreadcrumbs(getInitialBreadcrumbs(t));
            setNavigationFilter(undefined);
            return;
        }

        const navigationTree = findNavigationTreeForSelectedAxis(
            breadcrumb.field,
            navigationTrees,
        );
        const breadcrumbNode = getCurrentBreadcrumbNode(
            breadcrumb.value,
            navigationTree,
        );
        if (!navigationTree || !breadcrumbNode) {
            return;
        }
        setNewBreadcrumbs(
            breadcrumbNode,
            breadcrumbs,
            navigationTree,
            queryFilters,
            setBreadcrumbs,
            setNavigationFilter,
        );
    };
